import {Injectable} from '@angular/core';
import {RequestService} from "@services/request.service";
import {BehaviorSubject, Observable} from "rxjs";
import {StorageHelperService} from "@services/storage-helper.service";
import {UrlService} from "@services/url.service";
import dayjs from "dayjs";
import {Router} from "@angular/router";
import {map} from "rxjs/operators";
import {PaymentService} from "@services/payment.service";
import moment from "moment";
import {CartService} from "@services/cart.service";
import {environment} from "@environment";

@Injectable({
    providedIn: 'root'
})
export class OrderService {

    private order = this.initialValue;
    private _orderBS: BehaviorSubject<any> = new BehaviorSubject(this.order);

    private steps = this.initialSteps();
    stepsBS: BehaviorSubject<any[]> = new BehaviorSubject(this.steps);

    currentStepBS: BehaviorSubject<any> = new BehaviorSubject(this.steps[0]);

    minJobValue = null;
    minJobValueBS: BehaviorSubject<number> = new BehaviorSubject(null);
    serviceRemoved: BehaviorSubject<any> = new BehaviorSubject(null);

    orderSummary;
    orderSummaryBS: BehaviorSubject<any> = new BehaviorSubject(null);

    editCustomerBS: BehaviorSubject<boolean> = new BehaviorSubject(false);

    private settings: any = {showHeaderSteps: true};
    settingsBS: BehaviorSubject<any> = new BehaviorSubject({});

    serviceRemovedReloadTimeSlot: BehaviorSubject<boolean> = new BehaviorSubject(false);

    get initialValue() {
        return {
            cart: null,
            network: null,
            postcode: null,
            licence: null,
            services: [],
            time_slot: null,
            selected_time_slot: null,
            selected_slot: null,
            title: null,
            first_name: null,
            last_name: null,
            email: null,
            mobile: null,
            contact_method_id: null,
            opt_in: null,
            applied_voucher: null,
            appliedVoucherCode: null,
            appliedVoucherObject: null,
            discountEmailSentStatus: null,

            address: null,
            single_line_address: null,
            billing_address: null,
            different_billing_address: false,
            notes: null,
            name_on_card: null,

            completed: false,
            ast_accessed: null,
            terms: null,
            sms_enabled: null,
            preferredContactMethod: null,

            activeStep: 1,

            authCustomer: null,
            terms_conditions: false,
            payment_intent: null,
            stripe_publishable_key: null,
            paypal_client_id: null,
            google_palce_id: null,
            check_a_trade_url: null,
            overAllRating: null,
            reviews: null,
            google_url: null,

            showHeaderSteps: true,
            visitedThankYouPage: false,
            lastUpdatedAt: null,

            additional_services: [],

        }
    }

    getTitle() {
        return [
            {
                value: 'Mr',
                label: 'Mr'
            },
            {
                value: 'Mrs',
                label: 'Mrs'
            },
            {
                value: 'Miss',
                label: 'Miss'
            },
            {
                value: 'Ms',
                label: 'Ms'
            },
            {
                value: 'Dr',
                label: 'Dr'
            },
            {
                value: 'Reverend',
                label: 'Reverend'
            },
            {
                value: 'Lord',
                label: 'Lord'
            },
            {
                value: 'Lady',
                label: 'Lady'
            },
            {
                value: 'Sir',
                label: 'Sir'
            },
            {
                value: 'Catholic Father',
                label: 'Catholic Father',
            },
            {
                value: 'Dame',
                label: 'Dame',
            }
        ];
    }

    initialSteps() {
        const stepsArray = [
            {
                id: 1,
                value: 'postcode',
                label: 'Enter postcode',
                completed: false,
                routerLink: '/postcode',
            },
            {
                id: 2,
                value: 'services',
                label: 'Choose services',
                completed: false,
                routerLink: '/service-selection',
            },
            {
                id: 3,
                value: 'availability',
                label: 'Select Date and Time',
                completed: false,
                routerLink: '/slot-selection',
            },
            {
                id: 4,
                value: 'checkout',
                label: 'Checkout',
                completed: false,
                routerLink: '/checkout',
            },
        ];

        return stepsArray;
    }

    get orderBS() {
        return this._orderBS as Observable<any>;
    }

    constructor(
        public requestService: RequestService,
        public storageHelperService: StorageHelperService,
        public urlService: UrlService,
        public router: Router,
        private paymentService: PaymentService,
        private cartService: CartService,
    ) {
        const order = this.storageHelperService.get('order');
        if (order) {
            this.setOrder(order);

            // delete if order completed
            if (order.completed) {
                this.reset();
            }

            // check if older than 15 mins - if so, reset form
            let lastAccessed = order.lastUpdatedAt;
            if (lastAccessed) {
                lastAccessed = dayjs(lastAccessed);
                let now = dayjs();
                if (now.diff(lastAccessed, 'minutes') > environment.cart_life_in_minutes) {
                    this.reset();
                }
            }

            if (order.postcode) {
                this.postcodeLookup({network: order.network, postcode: order.postcode}).subscribe();
            }

            if (order.services.length) {
                this.calculateBasketQuantityAndCost();
            }

            this.minJobValueBS.subscribe((minJobValue) => {
                this.minJobValue = minJobValue;
            });
        }

    }

    reset() {
        this.storageHelperService.remove('order');
        this.setOrder(this.initialValue);
    }

    setOrder(order: any) {
        this.order = order;
        // this.order.lastUpdatedAt = dayjs().toDate();
        this._orderBS.next(order);
        this.storageHelperService.set('order', order);
    }

    postcodeLookup(params: any = {}) {
        return this.requestService.get('/api/ecom/v1/lookup', params, 'nitro').pipe(map((response) => {
            this.order.postcode = params.postcode;
            this.order.licence = response.license;
            this.order.terms = response.term;
            this.order.sms_enabled = response.sms_enabled;
            if (this.order.sms_enabled === false) {
                this.order.preferredContactMethod = 'email';
            }
            if (this.order.network !== 'tg') {
                this.order.stripe_publishable_key = response.stripe_publishable_key;
                this.order.paypal_client_id = response.paypal_client_id;
            }
            this.setOrder(this.order);
        }));
    }

    calculateBasketQuantityAndCost() {
        let quantity = 0;
        let grossValue = 0;
        this.order?.services?.forEach((serviceItem) => {
            quantity += serviceItem.quantity ? serviceItem.quantity : 0;
            if (serviceItem.quantity) {
                grossValue += parseFloat(serviceItem.service.gross_value || serviceItem.service.gross || serviceItem.service.value) * serviceItem.quantity;
            }
        });

        this.order?.additional_services?.forEach((serviceItem) => {
            quantity += serviceItem.quantity ? serviceItem.quantity : 0;
            if (serviceItem.quantity) {
                grossValue += parseFloat(serviceItem.service.gross_value || serviceItem.service.value) * serviceItem.quantity;
            }
        });

        // Todo
        if (this.order.appliedVoucherObject) {
            let discountValue = grossValue * this.order.appliedVoucherObject.percent_discount / 100;
            grossValue = grossValue - discountValue;
        }
        this.orderSummary = {
            gross: grossValue,
        };
        this.orderSummaryBS.next(this.orderSummary);
    }

    // (params) {
    //   this.requestService.get('/api/ecom/v1/lookup', params, 'nitro').subscribe(response => {
    //     return response;
    //   });
    // }

    getInstance(id, token) {
        return this.requestService.get('/api/ecom/v1/franchise/instance/read/' + id, null, 'nitro', token);
    }

    // setCustomerInstance(instance) {
    //   this.order.authCustomer.instance = instance;
    //   this.setOrder(this.order);
    //   // this.getInstance(order.authCustomer.instance_id, order.licence?.token).subscribe(response => {
    //   //   order.authCustomer.instance = response.instance;
    //   //   this.setOrder(order);
    //   // });
    // }

    patchSettings(settings: any) {
        this.settingsBS.next(Object.assign(this.settings, settings));
    }

    updatePaymentIndent() {
        let paymentParams;
        if (this.order.payment_intent) {
            paymentParams = {
                secret: this.order.payment_intent?.id,
                value: this.orderSummary.gross,
                order: this.order,
            };
        } else {
            paymentParams = {
                value: this.orderSummary.gross,
            };
        }

        const save =
            this.order.payment_intent ? this.paymentService.updatePaymentIntent(paymentParams, this.order.licence?.token) :
                this.paymentService.createPaymentIntent(paymentParams, this.order.licence?.token);

        return save.pipe(map(response => {
            this.order.payment_intent = response.intent;
            this.order.lastUpdatedAt = dayjs().toDate();
            this.setOrder(this.order);
        }));

    }

    setOrderSummary(orderSummary) {
        this.orderSummary = orderSummary;
        this.orderSummaryBS.next(orderSummary);
    }

    getCart(order) {
        const cart: any = {}
        cart.id = order.cart?.id;
        cart.postcode = order.postcode;
        cart.licence = order.licence;
        if (order && order.selected_slot) {
            if (order.network == 'mm') {
                cart.time_slot = moment(order.selected_slot).format("HH:mm:ss");
                cart.time_slot = dayjs(order.selected_time_slot).format('YYYY-MM-DD') + ' ' + cart.time_slot;
            } else if (order.network == 'sm') {
                cart.time_slot = moment(order.selected_slot.start_time).format("HH:mm:ss");
                cart.time_slot = dayjs(order.selected_time_slot.start_time).format('YYYY-MM-DD') + ' ' + cart.time_slot;
            }
        } else {
            order.time_slot = null;
        }

        cart.title = order.title;
        cart.first_name = order.first_name;
        cart.last_name = order.last_name;
        cart.email = order.email;
        cart.mobile_number = order.mobile;
        cart.notes = order.customer_notes;
        cart.contact_method = order.contact_method;
        cart.opt_in = order.opt_in;
        cart.applied_voucher = order.appliedVoucherCode;
        cart.appliedVoucherObject = order.appliedVoucherObject;

        cart.address = order.address;
        cart.billing_address = order.billing_address && order.billing_address.billingAddress;
        if (cart.billing_address) {
            cart.billing_address.is_billing_address = !!order.billing_address;
        }
        cart.services = order.services;

        return cart;
    }

    saveToCart(cart, order, time_slot) {
        if (cart) {
            order.title = cart.title;
            order.first_name = cart.first_name;
            order.last_name = cart.last_name;
            order.address = cart.address;
            order.billing_address = cart.billing_address && cart.billing_address.billingAddress;
            order.different_billing_address = !!cart.billingPostcode;
            order.email = cart.email;
            order.mobile = cart.mobile;
            order.opt_in = cart.opt_in;
            order.customer_notes = cart.customer_notes;
            if (cart.contact_method == 'both') {
                order.contact_method_id = 1;
            } else if (cart.contact_method == 'email') {
                order.contact_method_id = 2;
            } else if (cart.contact_method == 'sms') {
                order.contact_method_id = 3;
            } else if (cart.contact_method == 'sms') {
                order.contact_method_id = 3;
            } else if (cart.contact_method == 'phone') {
                order.contact_method_id = 4;
            }
        }
        if (order && order.selected_slot) {
            if (order.network == 'mm') {
                time_slot = moment(order.selected_slot, ["h:mm A"]).format("HH:mm:ss");
                order.time_slot = dayjs(order.selected_time_slot).format('YYYY-MM-DD') + ' ' + time_slot;
            } else if (order.network == 'sm') {
                time_slot = moment(order.selected_slot.start_time).format("HH:mm:ss");
                order.time_slot = dayjs(order.selected_time_slot.start_time).format('YYYY-MM-DD') + ' ' + time_slot;
            }
        } else {
            order.time_slot = null;
        }

        return order;
    }

    saveCart(order) {
        let cart = this.getCart(order);
        this.cartService.save(cart).subscribe((response) => {
            this.order.cart = {
                id: response.cart.id,
            }
            this.setOrder(this.order);
        }, (errors) => {
        });
    }

}
